
import { Button } from "@/components/ui/button"
import { Trash, BarChart2, Loader2 } from "lucide-react"
import { useTheme } from "@/contexts/ThemeContext"
import { Progress } from "@/components/ui/progress"
import { useAuth } from "@/contexts/AuthContext"

interface TableActionBarProps {
  selectedRowsCount: number;
  onDeleteSelected: () => void;
  showUpdateMetricsButton?: boolean;
  onUpdateMetrics?: () => void;
  isUpdatingMetrics?: boolean;
  metricsUpdateProgress?: string;
  updateProgress?: {
    current: number;
    total: number;
    url: string;
  } | null;
}

export function TableActionBar({ 
  selectedRowsCount, 
  onDeleteSelected,
  onUpdateMetrics,
  showUpdateMetricsButton = false,
  isUpdatingMetrics = false,
  metricsUpdateProgress,
  updateProgress,
}: TableActionBarProps) {
  const { theme } = useTheme();
  const { hasBuAccess } = useAuth();
  const isLightMode = theme === 'light';

  const percentage = updateProgress && updateProgress.total > 0 
    ? Math.round((updateProgress.current / updateProgress.total) * 100) 
    : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {selectedRowsCount > 0 && !hasBuAccess && (
          <>
            <span className={`text-sm mr-2 ${isLightMode ? 'text-gray-600' : 'text-muted-foreground dark:text-[#8E9196]'}`}>
              {selectedRowsCount} {selectedRowsCount === 1 ? 'row' : 'rows'} selected
            </span>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={onDeleteSelected}
              className="h-10"
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </>
        )}
        
        {showUpdateMetricsButton && onUpdateMetrics && !hasBuAccess && (
          <div className="flex items-center gap-3">
            <Button 
              variant={isLightMode ? "default" : "outline"} 
              onClick={onUpdateMetrics}
              className={`h-10 ${isLightMode ? 'bg-black text-white hover:bg-gray-800' : 'dark:text-white dark:bg-transparent'}`}
              disabled={isUpdatingMetrics}
            >
              {isUpdatingMetrics ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <BarChart2 className="h-4 w-4 mr-1" />
              )}
              {isUpdatingMetrics ? 'Updating...' : 'Update Engagement Metrics'}
            </Button>
            
            {isUpdatingMetrics && updateProgress && (
              <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/30 px-3 py-2 rounded-md border border-blue-200 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {percentage}% ({updateProgress.current}/{updateProgress.total})
                </div>
                <div className="w-32">
                  <Progress value={percentage} className="h-1.5" />
                </div>
              </div>
            )}
          </div>
        )}
        
        {hasBuAccess && (selectedRowsCount > 0 || showUpdateMetricsButton) && (
          <div className="text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-md">
            BU Access - Actions Disabled
          </div>
        )}
      </div>
      
      {isUpdatingMetrics && updateProgress?.url && !hasBuAccess && (
        <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20 px-3 py-1 rounded border border-blue-200 dark:border-blue-800">
          <span className="font-medium">Processing:</span> {updateProgress.url}
        </div>
      )}
    </div>
  );
}
